// ABOUTME: State machine for the two-step CPQ configuration chain (step 1 → resolve-chain → step 2).
// ABOUTME: Owns the step state and the discard confirmation, and delegates the chain's persistence to the caller.

import { TUserAssignment } from '@coolshop/cool-configurator';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ChainStep, parseLineItemConfiguration } from '../types';
import { ChainStepSeed, resolveChainStepSeed } from '../utils/resolveChainStepSeed';
import { useResolveChain } from './useResolveChain';

/** How the current step's drawer treats the assignments passed to it. */
type StepMode = 'add' | 'edit';

export interface FlowStep {
    prototypeId: string;
    userAssignments: TUserAssignment[];
    /** Configit view the step was configured against, so reopening it uses the same one. */
    viewId?: string;
    /** Set once the step has been finalized, in this session or in a previous one. */
    configurationJson?: string;
    productId?: string;
    /**
     * 'edit' keeps the passed assignments authoritative; 'add' lets the drawer merge the seller's
     * configurator defaults over them, which is only right for a step opened from scratch.
     */
    mode: StepMode;
}

/** Which entry point opened the flow, and therefore how a save persists. */
type FlowKind = 'add' | 'edit' | 'view';

export interface FlowSession {
    kind: FlowKind;
    /** The line item this session shows: set for 'edit' and 'view', absent while adding. */
    lineItemId?: string;
    /** Header title override, for entry points whose own wording differs from the mode's. */
    title?: string;
    steps: FlowStep[];
    currentStep: number;
}

/**
 * What the seller asked for when the finalize was triggered. Defaults to 'save' so a finalize that
 * did not come from one of the flow's own buttons (the package's own button, on tenants without
 * chaining) still saves the line item.
 */
type Intent = 'next' | 'back' | 'save';

/** Set while the seller has to confirm dropping an already-configured second step. */
type PendingDiscard = { kind: 'advance'; seed: ChainStepSeed } | { kind: 'save' };

/**
 * A finalized chain to persist. Its last step is the effective configuration — the one that prices
 * the line and fills configuration_json. Where it lands (an offer, a new offer, the cart) is the
 * entry point's decision, so the flow hands it back through `persist` rather than saving itself.
 */
export interface ChainPersistArgs {
    /** Which entry point opened the flow. 'view' never persists. */
    kind: FlowKind;
    /** Set for 'edit': the line item being reconfigured. */
    lineItemId?: string;
    /** The ordered chain, its last step effective. */
    chain: ChainStep[];
    /** The last (effective) step of the chain. */
    lastStep: ChainStep;
}

interface UseChainedConfiguratorFlowInput {
    /**
     * Persists the finalized chain. The entry point decides where it lands (an offer line item, a
     * freshly created offer, the active cart) and surfaces any pricing warning; the flow only
     * captures the steps and hands the resulting chain over.
     */
    persist: (args: ChainPersistArgs) => Promise<void>;
    /** Drives the caller's loading backdrop across the network waits of this flow. */
    setBusy: (busy: boolean) => void;
    /**
     * Wraps a save behind a precondition so it can be deferred and replayed once met — the offer
     * flow uses it to require a client first. Defaults to running the save immediately.
     */
    gate?: (proceed: () => void) => void;
}

const toChainStep = (step: FlowStep | undefined): ChainStep | undefined =>
    step?.configurationJson
        ? {
              productId: step.productId ?? '',
              prototypeId: step.prototypeId,
              configurationJson: step.configurationJson,
          }
        : undefined;

const fromChainStep = (step: ChainStep): FlowStep => {
    const configuration = parseLineItemConfiguration(step.configurationJson);
    return {
        prototypeId: step.prototypeId,
        userAssignments: configuration?.userAssignments ?? [],
        viewId: configuration?.viewId,
        configurationJson: step.configurationJson,
        productId: step.productId,
        // A persisted step's selections are authoritative, so they are never re-defaulted.
        mode: 'edit',
    };
};

/**
 * Drives a configuration chain of up to two steps on a single line item.
 *
 * Both configurations are persisted on that one line item, and the last one is the effective
 * configuration: it fills configuration_json and is what prices the line. The seller moves between
 * the steps with Back/Next, and can stop at the first one ("Save line item"), which reduces the
 * stored chain back to a single step.
 */
export function useChainedConfiguratorFlow({
    persist,
    setBusy,
    gate = proceed => proceed(),
}: UseChainedConfiguratorFlowInput) {
    const { formatMessage } = useIntl();
    const resolveChain = useResolveChain();

    // Mirrored in a ref because the finalize handler reads and rewrites the session inside an async
    // sequence, where the state value it closed over is already stale.
    const sessionRef = useRef<FlowSession | null>(null);
    const [session, setSessionState] = useState<FlowSession | null>(null);
    const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(null);
    const intentRef = useRef<Intent>('save');

    const setSession = useCallback((next: FlowSession | null) => {
        sessionRef.current = next;
        setSessionState(next);
    }, []);

    const close = useCallback(() => {
        setPendingDiscard(null);
        setSession(null);
    }, [setSession]);

    /** Opens a fresh configuration, from the catalog or prefilled from a customer's material. */
    const openAdd = useCallback(
        ({
            prototypeId,
            initialUserAssignments = [],
            mode = 'add',
            title,
        }: {
            prototypeId: string;
            initialUserAssignments?: TUserAssignment[];
            mode?: StepMode;
            title?: string;
        }) => {
            setSession({
                kind: 'add',
                title,
                steps: [{ prototypeId, userAssignments: initialUserAssignments, mode }],
                currentStep: 0,
            });
        },
        [setSession],
    );

    /** Reopens a line item's chain for editing, on its last (effective) step. */
    const openEdit = useCallback(
        ({ lineItemId, chain }: { lineItemId: string; chain: ChainStep[] }) => {
            if (chain.length === 0) return;
            setSession({
                kind: 'edit',
                lineItemId,
                steps: chain.map(fromChainStep),
                currentStep: chain.length - 1,
            });
        },
        [setSession],
    );

    /**
     * Opens a line item's chain read-only, on its last (effective) step. Without `lineItemId` the
     * configurator resolves the quote from the offer alone, e.g. for a line of an order.
     */
    const openView = useCallback(
        ({ lineItemId, chain }: { lineItemId?: string; chain: ChainStep[] }) => {
            if (chain.length === 0) return;
            setSession({
                kind: 'view',
                // Carried even though a view never persists: the configurator still resolves the
                // configuration, so the CAD drawing it can produce is stamped with the line it shows.
                lineItemId,
                steps: chain.map(fromChainStep),
                currentStep: chain.length - 1,
            });
        },
        [setSession],
    );

    /**
     * Triggers a finalize for the given intent. The drawer hands `requestFinalize` to its header
     * actions; the resulting selections come back through handleFinalize.
     */
    const request = useCallback((intent: Intent, requestFinalize: () => void) => {
        intentRef.current = intent;
        requestFinalize();
    }, []);

    /** Pages between the steps of a read-only chain, where nothing has to be captured. */
    const goToStep = useCallback(
        (index: number) => {
            const { current } = sessionRef;
            if (!current || !current.steps[index]) return;
            setSession({ ...current, currentStep: index });
        },
        [setSession],
    );

    const performSave = useCallback(
        async (steps: FlowStep[], upToStep: number) => {
            const { current } = sessionRef;
            if (!current) return;
            const chain = steps
                .slice(0, upToStep + 1)
                .map(toChainStep)
                .filter((step): step is ChainStep => Boolean(step));
            const lastStep = chain[chain.length - 1];
            if (!lastStep) return;

            // Closed before the save, like every other configurator save: the caller's backdrop
            // covers the gap until the table refreshes.
            const { kind, lineItemId } = current;
            close();
            setBusy(true);
            try {
                await persist({ kind, lineItemId, chain, lastStep });
            } finally {
                setBusy(false);
            }
        },
        [persist, close, setBusy],
    );

    const applySeed = useCallback(
        (seed: ChainStepSeed, steps: FlowStep[]) => {
            const { current } = sessionRef;
            if (!current) return;
            const existing = steps[1];
            const stepTwo: FlowStep =
                seed.action === 'keep' && existing
                    ? { ...existing, userAssignments: seed.userAssignments, mode: 'edit' }
                    : {
                          prototypeId: seed.prototypeId,
                          userAssignments: seed.userAssignments,
                          mode: 'add',
                      };
            setSession({ ...current, steps: [steps[0], stepTwo], currentStep: 1 });
        },
        [setSession],
    );

    const advance = useCallback(
        async (steps: FlowStep[], configurationJson: string) => {
            setBusy(true);
            try {
                const resolution = await resolveChain(configurationJson);
                if (!resolution) {
                    SnackBarUtils.info(
                        formatMessage({
                            id: 'cpq.configurator.no-next-product',
                            defaultMessage:
                                'This configuration does not continue into a second product. Use "Save line item" to save it.',
                        }),
                    );
                    return;
                }
                const seed = resolveChainStepSeed(resolution, toChainStep(steps[1]));
                // A step 2 the seller already configured is only replaced with their consent.
                if (seed.action === 'seed' && steps[1]?.configurationJson) {
                    setPendingDiscard({ kind: 'advance', seed });
                    return;
                }
                applySeed(seed, steps);
            } catch {
                SnackBarUtils.error(
                    formatMessage({
                        id: 'generic-error',
                        defaultMessage: 'An unexpected error occurred.',
                    }),
                );
            } finally {
                setBusy(false);
            }
        },
        [resolveChain, applySeed, formatMessage, setBusy],
    );

    /**
     * Every Next / Back / Save goes through a finalize, so the step's current selections are
     * captured before the flow moves on or persists.
     */
    const handleFinalize = useCallback(
        async (configurationJson: string, productId: string) => {
            const { current } = sessionRef;
            if (!current) return;
            const intent = intentRef.current;
            intentRef.current = 'save';

            const index = current.currentStep;
            const finalized = parseLineItemConfiguration(configurationJson);
            // The finalized selections replace the step's own, so a Back/Next round trip reopens
            // the step on what the seller last had rather than on what it opened with.
            const steps = current.steps.map((step, i) =>
                i === index
                    ? {
                          ...step,
                          configurationJson,
                          productId,
                          userAssignments: finalized?.userAssignments ?? step.userAssignments,
                          viewId: finalized?.viewId,
                          mode: 'edit' as const,
                      }
                    : step,
            );

            if (intent === 'back') {
                setSession({ ...current, steps, currentStep: Math.max(index - 1, 0) });
                return;
            }
            // The capture has to land before the async branches below, which read the session back
            // from the ref.
            setSession({ ...current, steps });

            if (intent === 'next') {
                await advance(steps, configurationJson);
                return;
            }
            // Saving from step 1 drops a second configuration the line item already had.
            if (index === 0 && steps[1]?.configurationJson) {
                setPendingDiscard({ kind: 'save' });
                return;
            }
            gate(() => performSave(steps, index));
        },
        [setSession, advance, performSave, gate],
    );

    const confirmDiscard = useCallback(async () => {
        const pending = pendingDiscard;
        const { current } = sessionRef;
        setPendingDiscard(null);
        if (!pending || !current) return;
        if (pending.kind === 'advance') {
            applySeed(pending.seed, current.steps);
            return;
        }
        gate(() => performSave(current.steps, 0));
    }, [pendingDiscard, applySeed, performSave, gate]);

    const cancelDiscard = useCallback(() => setPendingDiscard(null), []);

    return {
        session,
        pendingDiscard,
        openAdd,
        openEdit,
        openView,
        close,
        request,
        goToStep,
        handleFinalize,
        confirmDiscard,
        cancelDiscard,
    };
}
